import React, { useCallback, useMemo } from "react";
import { Alert } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { v4 as uuidv4 } from "uuid";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { usePropertyLocales } from "../../components/property-locale-provider";
import { useProperty } from "../../components/property-provider";
import originalSchema from "../../gen/schema/witbooking.backoffice.web_message.v1/WebMessage.json";
import {
  WebMessagePositionType as WebMessagePositionTypeProto,
  WebMessageType as WebMessageTypeProto,
} from "../../gen/witbooking/backoffice/webmessage/v1/webmessage_api_pb";
import actions from "../../lib/actions";
import { getLanguageFromFullLocale } from "../../lib/config";
import { SECTION } from "../../lib/constants";
import { useApiClient } from "../../lib/http";
import { regenPropertyCache } from "../common/api";
import { FormConfiguration } from "../shared/crud/form";
import { inputFilter } from "../shared/crud/filter-widgets";
import { TableColumns, TableFilters } from "../shared/crud/list";
import SimpleCrud, { defaultCrudMessages } from "../shared/crud/simple-crud";
import * as TableRenderers from "../shared/renderers/table";
import {
  BooleanRenderer,
  mapRenderer,
  ReadOnlySwitchRenderer,
  StringRenderer,
} from "../shared/renderers/table";
import { DateRangePicker } from "../shared/widgets/dateRangePicker";
import { DateRangeStringRenderer } from "../shared/widgets/DateRangeStringRenderer";
import { HtmlEditorWidget } from "../shared/widgets/html";
import SectionUnderline from "../shared/widgets/sectionUnderline";
import { SwitchGroup } from "../shared/widgets/SwitchGroup";
import { translationFieldConfig } from "../shared/widgets/translation";
import { WebMessageEntity } from "./domain/webMessage.entity";
import {
  createEntity,
  deleteEntity,
  getAllEntities,
  getEntity,
  updateEntity,
} from "./infrastructure/webMessage.api";
import {
  tableHeaderMessages,
  webMessageMessages,
  WebMessagePositionType,
  WebMessagePositionTypeMapping,
  webMessagePositionTypeMessages,
  WebMessageType,
  webMessageTypeMessages,
} from "./messages";

const schema = {
  ...originalSchema,
  properties: {
    ...originalSchema.properties,
    validitySection: {
      type: "string",
    },
    positionType: {
      enum: [
        "TOP_INVENTORY_STEP_1",
        1,
        "MODAL_STEP_1",
        5,
        "MODAL_STEP_2",
        6,
        "BELOW_PERSONAL_INFO_STEP_2",
        3,
        "TOP_METHOD_PAYMENT_STEP_2",
        4,
        "CONFIRMATION_PAGE",
        7,
      ],
      oneOf: [
        {
          type: "string",
        },
        {
          type: "integer",
        },
      ],
    },
    type: {
      enum: ["INFO", 1, "NOTICE", 2, "ERROR", 3, "SUCCESS", 4],
      oneOf: [
        {
          type: "string",
        },
        {
          type: "integer",
        },
      ],
    },
  },
};

const messages = {
  ...defaultCrudMessages,
  ...webMessageMessages,
  ...webMessageTypeMessages,
  ...webMessagePositionTypeMessages,
  tableHeaders: tableHeaderMessages,
  warning: {
    id: "webMessages.warning",
    defaultMessage:
      "Taxes and Citytax must be create in Settings > Parameterization > Rates. To avoid duplicates, please Do NOT include this information in Web messages.",
  },
};
interface WebMessageFilters {
  ticker: string;
}
function buildFormConfiguration(allowedLocales: string[]): FormConfiguration<WebMessageEntity> {
  return {
    __fieldOrder: [
      "ticker",
      "id",
      "active",
      "translationsList",
      "positionType",
      "type",
      "unlockCode",
      "showOnlyOnUnavailable",
      "validitySection",
      "bookingWindowPeriod",
      "*",
    ],
    __useProtoStyleArrays: true,
    id: {
      default: () => uuidv4(),
      hidden: true,
    },
    ticker: {
      // Legacy messages predate the field, so keep it editable until it holds a value.
      readonly: (isNew, formData) => !isNew && !!formData?.ticker,
      messages: {
        label: messages.ticker,
      },
    },
    active: {
      widget: SwitchGroup,
      messages: {
        label: messages.active,
      },
    },
    positionType: {
      messages: {
        label: messages.webMessagePositionType,
      },
      default: () => WebMessagePositionTypeProto.TOP_INVENTORY_STEP_1,
      enumLabels: [
        messages[WebMessagePositionType.TOP_INVENTORY_STEP_1],
        messages[WebMessagePositionType.MODAL_STEP_1],
        messages[WebMessagePositionType.MODAL_STEP_2],
        messages[WebMessagePositionType.BELOW_PERSONAL_INFO_STEP_2],
        messages[WebMessagePositionType.TOP_METHOD_PAYMENT_STEP_2],
        messages[WebMessagePositionType.CONFIRMATION_PAGE],
      ],
    },
    type: {
      messages: {
        label: messages.type,
      },
      default: () => WebMessageTypeProto.INFO,
      hidden: true,
      enumLabels: [
        messages[WebMessageType.INFO],
        messages[WebMessageType.NOTICE],
        messages[WebMessageType.ERROR],
        messages[WebMessageType.SUCCESS],
      ],
    },
    translationsList: {
      ...translationFieldConfig(allowedLocales || []),
      children: {
        __fieldOrder: ["description", "*"],
        description: {
          widget: HtmlEditorWidget,
          messages: {
            label: messages.webMessageTranslationsDescriptionLabel,
          },
        },
        ...translationFieldConfig(allowedLocales || []).children,
      },
      messages: {
        label: messages.webMessageTranslationsTitle,
      },
    },
    showOnlyOnUnavailable: {
      widget: SwitchGroup,
      messages: {
        label: messages.showOnlyOnUnavailable,
      },
    },
    validitySection: {
      fieldWidget: SectionUnderline,
      messages: {
        label: messages.validity,
      },
    },
    unlockCode: {
      messages: {
        label: messages.unlockCode,
      },
    },
    bookingWindowPeriod: {
      fieldWidget: DateRangePicker,
    },
  };
}
function WebMessageCRUD() {
  const allowedLocales = usePropertyLocales();
  const intl = useIntl();
  const property = useProperty();
  const httpClientCache = useApiClient();

  const tableColumns = useMemo(() => buildTableColumns(), []);
  const tableFilters = useMemo(() => buildTableFilters(), []);
  const formConfiguration = useMemo(() => buildFormConfiguration(allowedLocales), [allowedLocales]);

  const regenCache = useCallback(
    () =>
      regenPropertyCache(httpClientCache, property.ticker).catch(error =>
        console.error("Error regenerating property cache", error)
      ),
    [httpClientCache, property.ticker]
  );

  const handleCreate = useCallback(
    async (entity: WebMessageEntity) => {
      const id = await createEntity(entity);
      regenCache();
      return id;
    },
    [regenCache]
  );

  const handleUpdate = useCallback(
    async (entity: WebMessageEntity) => {
      await updateEntity(entity);
      regenCache();
    },
    [regenCache]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      await deleteEntity(id);
      regenCache();
    },
    [regenCache]
  );
  const validate = useCallback(
    (formData, errors) => {
      if (
        !formData.bookingWindowPeriod?.from ||
        !formData.bookingWindowPeriod?.to ||
        formData.bookingWindowPeriod?.to <= formData.bookingWindowPeriod?.from
      ) {
        errors.bookingWindowPeriod.addError(intl.formatMessage(messages.validityError));
      }
      return errors;
    },
    [intl]
  );

  if (allowedLocales === undefined) {
    return null;
  }

  return (
    <>
      <Alert variant="danger">
        <strong>
          <FormattedMessage {...messages.warning} />
        </strong>
      </Alert>
      <SimpleCrud<WebMessageEntity, WebMessageFilters>
        formConfiguration={formConfiguration}
        schema={schema}
        tableColumns={tableColumns}
        tableFilters={tableFilters}
        messages={messages}
        onGetAll={getAllEntities}
        onGetOne={getEntity}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        hideExportButton
        canCreate={actions.CreateWebMessage}
        canUpdate={actions.ModifyWebMessage}
        section={SECTION.WEB_MESSAGES}
        listHeight={"100%"}
        validate={validate}
      />
    </>
  );
}
function buildTableColumns(): TableColumns<WebMessageEntity> {
  return {
    ticker: {
      renderer: StringRenderer,
      width: 150,
    },
    active: {
      renderer: ReadOnlySwitchRenderer,
      width: 100,
    },
    positionType: {
      renderer: mapRenderer(StringRenderer, (v, intl) =>
        intl.formatMessage(messages[WebMessagePositionTypeMapping[v]])
      ),
      width: 250,
    },
    translationsList: {
      renderer: TableRenderers.mapRenderer(
        TableRenderers.StringRenderer,
        (v, intl) => {
          if (!v || v.length === 0) return undefined;
          const current = v.find(t => t.locale === getLanguageFromFullLocale(intl.locale));
          return current?.description || v[0].description;
        },
        { isHtml: true, maxCharacters: 300 }
      ),
    },
    showOnlyOnUnavailable: {
      renderer: BooleanRenderer,
      width: 250,
    },
    bookingWindowPeriod: {
      renderer: DateRangeStringRenderer,
      width: 200,
    },
  };
}

function buildTableFilters(): TableFilters<WebMessageEntity, WebMessageFilters> {
  return {
    ticker: {
      filterFn: (data: string, filter: string) => data.toLowerCase().includes(filter.toLowerCase()),
      widget: inputFilter,
    },
  };
}

export default withAuthorizationVerifier(WebMessageCRUD);
